import React from 'react';
import { VStack, HStack, Heading, Text, Badge, SimpleGrid, Box, Divider, Spacer, Button, Flex } from '@chakra-ui/react';
import { MdSearch, MdEdit, MdVisibility } from 'react-icons/md';
import { VirtualizedGridConfig, FilterField } from '../../components/VirtualizedGrid';

// Configuración del grid para infracciones
export const infraccionesGridConfig: VirtualizedGridConfig = {
  itemsPerRow: 4,
  itemHeight: 450,
  containerHeight: 2250, // 20 items = 5 filas * 450px altura
  searchFields: [
    'folio',
    'ciudadano',
    'numeroPlaca',
    'nombreOficial',
    'lugarInfraccion'
  ]
};

// Campos de filtro para infracciones
export const infraccionesFilterFields: FilterField[] = [
  {
    key: 'texto',
    type: 'text',
    placeholder: 'Buscar por folio, conductor, placa u oficial',
    icon: <MdSearch color="gray.300" />
  },
  {
    key: 'estatus',
    type: 'select',
    placeholder: 'Estatus de infracción',
    options: [
      { value: 'PENDIENTE', label: 'Pendiente' },
      { value: 'PAGADA', label: 'Pagada' },
      { value: 'VENCIDA', label: 'Vencida' }
    ]
  },
  {
    key: 'nombreOficial',
    type: 'text',
    placeholder: 'Oficial que registró'
  },
  {
    key: 'fechaInfraccion',
    type: 'date',
    placeholder: 'Fecha específica'
  }
];

// Funciones de utilidad
const getEstatusColor = (estatus: string) => {
  switch (estatus?.toLowerCase()) {
    case 'pendiente': return '#bb8c52';
    case 'pagada':
    case 'pagado': return '#48bb78';
    case 'vencida': return '#f56565';
    default: return 'gray';
  }
};

const getGarantiaColor = (tipoGarantia: string) => {
  switch (tipoGarantia?.toLowerCase()) {
    case 'licencia': return '#5b182b';
    case 'placa': return '#691f30';
    case 'vehiculo': return '#e53e3e';
    default: return 'gray';
  }
};

// Función render para las tarjetas de infracciones
export const renderInfraccionCard = (
  infraccion: any, 
  index: number, 
  onEdit: (infraccion: any) => void,
  onView: (infraccion: any) => void
) => {
  return (
    <VStack align="stretch" spacing={3}>
      {/* Header */}
      <Flex align="center">
        <VStack align="start" spacing={0}>
          <Heading size="md" color="#5b182b">
            {infraccion.folio || `ID-${infraccion.id}`}
          </Heading>
          <Text fontSize="sm" color="gray.500">
            Infracción de Tránsito
          </Text>
        </VStack>
        <Spacer />
        <Text fontSize="xs" color="gray.400">
          #{infraccion.id}
        </Text>
      </Flex>

      <Divider />

      {/* Información Principal */}
      <VStack align="stretch" spacing={2}>
        <SimpleGrid columns={2} spacing={3}>
          <Box>
            <Text fontWeight="semibold" fontSize="sm" color="gray.700">
              Conductor
            </Text>
            <Text fontSize="sm">
              {infraccion.ciudadano || 'Sin datos'}
            </Text>
          </Box>
          <Box>
            <Text fontWeight="semibold" fontSize="sm" color="gray.700">
              Fecha
            </Text>
            <Text fontSize="sm">
              {infraccion.fechaInfraccion ? 
                new Date(infraccion.fechaInfraccion).toLocaleDateString('es-ES') : 
                'Sin fecha'
              }
            </Text>
          </Box>
        </SimpleGrid>
        
        <SimpleGrid columns={2} spacing={3}>
          <Box>
            <Text fontWeight="semibold" fontSize="sm" color="gray.700">
              Placa
            </Text>
            <Text fontSize="sm" fontWeight="bold" color="#5b182b">
              {infraccion.numeroPlaca || 'Sin placa'}
            </Text>
          </Box>
          <Box>
            <Text fontWeight="semibold" fontSize="sm" color="gray.700">
              Vehículo
            </Text>
            <Text fontSize="sm">
              {infraccion.marca && infraccion.modeloVehiculo 
                ? `${infraccion.marca} ${infraccion.modeloVehiculo}`
                : infraccion.carroceria?.descripcion || 'Sin datos'
              }
            </Text>
          </Box>
        </SimpleGrid>

        <Box>
          <Text fontWeight="semibold" fontSize="sm" color="gray.700">
            Lugar de Infracción
          </Text>
          <Text fontSize="sm" noOfLines={2}>
            {infraccion.lugarInfraccion || 'Sin ubicación'}
          </Text>
        </Box>

        <Box>
          <Text fontWeight="semibold" fontSize="sm" color="gray.700">
            Oficial
          </Text>
          <Text fontSize="sm">
            {infraccion.nombreOficial ? 
              `${infraccion.gradoOficial || ''} ${infraccion.nombreOficial}`.trim() :
              infraccion.gradoOficial || 'Sin asignar'
            }
          </Text>
        </Box>
      </VStack>

      <Divider />

      {/* Estados y Garantía */}
      <VStack align="stretch" spacing={2}>
        <HStack justify="space-between">
          <Text fontWeight="semibold" fontSize="sm" color="gray.700">
            Estatus
          </Text>
          <Badge 
            bg={getEstatusColor(infraccion.estatus)}
            color="white"
            variant="solid"
            borderRadius="full"
          >
            {infraccion.estatus || 'Pendiente'}
          </Badge>
        </HStack>

        <HStack justify="space-between">
          <Text fontWeight="semibold" fontSize="sm" color="gray.700">
            Garantía
          </Text>
          <Badge 
            bg={getGarantiaColor(infraccion.tipoGarantia || '')}
            color="white"
            variant="solid"
            borderRadius="full"
          >
            {infraccion.tipoGarantia || 'Sin garantía'}
          </Badge>
        </HStack>

        {infraccion.tipoGarantia === 'vehiculo' && (
          <HStack justify="space-between">
            <Text fontWeight="semibold" fontSize="sm" color="gray.700">
              Corralón
            </Text>
            <Text fontSize="sm" color="#e53e3e" fontWeight="bold">
              {infraccion.corralon || 'Sin asignar'}
            </Text>
          </HStack>
        )}
      </VStack>

      <Divider />

      {/* Acciones */}
      <HStack spacing={2} justify="center">
        <Button
          size="sm"
          color="#5b182b"
          borderColor="#5b182b"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(infraccion);
          }}
          leftIcon={<MdEdit />}
        >
          Editar
        </Button>

        <Button
          size="sm"
          color="#977245ff"
          borderColor="#bb8c52"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            onView(infraccion);
          }}
          leftIcon={<MdVisibility />}
        >
          Ver
        </Button>
      </HStack>
    </VStack>
  );
};